import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package, Users, FileText, Upload, LogOut, BarChart3, Database, TrendingUp,
  Settings, Truck, ClipboardList, Trash2, HardDrive, FileInput, FileOutput,
  Warehouse, BookOpen, MapPin, Boxes, Factory, Shirt, TestTube, Sparkles,
  Undo2, Calculator, Scissors, Home, Zap, Monitor, Wrench, ShoppingCart,
  CircleDot, Send, Printer, MessageSquare, Activity, Archive, PackageCheck,
  Search, RefreshCw, ArrowLeftRight, Building2, Clock, List, Bell, Timer,
  IndianRupee, UserCheck, MonitorSmartphone, Contact, Briefcase, Grid3x3, ReceiptText, FileCode2, Trophy,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import * as authApi from '@/services/authApi';
import apiClient from '@/services/apiClient';

// Static catalog mirror — keeps the dashboard functional if the catalog
// endpoint blips. Authoritative source is `/api/dispatch-sections/catalog`.
const PER_SECTION_DISPATCH_CATALOG = [
  { slug: 'color_combo', title: 'Color Combo Section', color: 'bg-pink-600' },
  { slug: 'art_silk',    title: 'Art Silk Section',    color: 'bg-rose-600' },
  { slug: 'kids',        title: 'Kids Section',        color: 'bg-orange-600' },
];

// ---------------------------------------------------------------------------
// Tile catalog
// ---------------------------------------------------------------------------
//   moduleId  → user must have this entry in `user.modules` (operational tiles)
//   opId      → user must have this entry in `user.allowed_operations[moduleId]`
//   adminOnly → tile is hidden for non-Super-Admin unless explicitly granted
//               via `user.allowed_operations.admin_panel = [opId]`
//   roles     → tile is shown only when `user.role` is in this list (overrides moduleId)
//   sections  → tile is shown when `user.sections` overlaps this list
// Super Admin (role ∈ {Admin, super_admin}) always sees every tile.
// ---------------------------------------------------------------------------
const moduleGroups = [
  // === User & Access (admin-only by default) ===
  {
    title: 'User & Access',
    icon: Users,
    modules: [
      { id: 'users', title: 'User Management', description: 'Manage users, roles, and permissions',
        icon: Users, route: '/admin/users', color: 'bg-blue-500',
        adminOnly: true, opId: 'users' },
      { id: 'site-management', title: 'Site Management',
        description: 'Multi-site control · Site Admin grants · Bootstrap checklist',
        icon: Building2, route: '/admin/site-management', color: 'bg-purple-600',
        adminOnly: true, opId: 'site-management' },
      { id: 'warehouses', title: 'Warehouses',
        description: 'Per-site warehouses & GPS coordinates for auto-tagging entries',
        icon: Building2, route: '/admin/warehouses', color: 'bg-emerald-600',
        adminOnly: true, opId: 'warehouses' },
      { id: 'dc-notifications', title: 'DC Alert Recipients',
        description: 'Who gets the Pick & Pack "create DC & transport" messenger alert',
        icon: Truck, route: '/admin/dc-notifications', color: 'bg-fuchsia-600',
        adminOnly: true, opId: 'dc-notifications' },
    ],
  },

  // === Operational tiles (formerly on /dashboard) ===
  {
    title: 'Daily Operations',
    icon: ClipboardList,
    modules: [
      { id: 'inward', title: 'Inward Entry', description: 'Record incoming goods',
        icon: FileInput, route: '/gate-entry/inward', color: 'bg-emerald-500',
        moduleId: 'inward' },
      { id: 'outward-jobworker', title: 'Outward (Job Worker)',
        description: 'Job Worker & Internal Transfer',
        icon: Truck, route: '/gate-entry/outward', color: 'bg-blue-500',
        moduleId: 'outward_jobworker' },
      { id: 'outward-customer', title: 'Outward (Customer)',
        description: 'Dispatch to customers',
        icon: Send, route: '/gate-entry/outward-customer', color: 'bg-orange-500',
        moduleId: 'outward_customer' },
      { id: 'drafts', title: 'Draft Entries',
        description: 'Continue editing your drafts',
        icon: Clock, route: '/drafts', color: 'bg-slate-500',
        moduleId: 'inward' },
      { id: 'my-entries', title: 'My Entries',
        description: 'View your submissions & sync status',
        icon: List, route: '/my-entries', color: 'bg-slate-600',
        moduleId: 'inward' },
      { id: 'documentation', title: 'Inward Documentation',
        description: 'AI-powered document extraction',
        icon: FileText, route: '/documentation', color: 'bg-indigo-500',
        moduleId: 'documentation' },
      { id: 'shipment-requests', title: 'Shipment Requests',
        description: 'Confirm shipments to sections',
        icon: Truck, route: '/shipment-requests', color: 'bg-cyan-500',
        moduleId: 'documentation' },
      { id: 'outward-documentation', title: 'Outward Documentation',
        description: 'AI-powered outward document extraction',
        icon: FileText, route: '/outward-documentation', color: 'bg-rose-500',
        moduleId: 'outward_documentation' },
      { id: 'customer-outward-docs', title: 'Customer Outward Docs',
        description: 'LR Details, Delivery & Freight for customer shipments',
        icon: FileText, route: '/customer-outward-documentation', color: 'bg-amber-500',
        moduleId: 'outward_customer' },
      { id: 'eway-bills', title: 'E-Way Bills',
        description: 'Generate, print & cancel GST e-way bills — invoices, DCs, branch transfers & job work',
        icon: ReceiptText, route: '/eway-bills', color: 'bg-teal-700',
        moduleId: 'eway_bills', ops: ['ewb-generate', 'ewb-invoice-upload'], roles: ['Site Admin', 'Section Incharge', 'Incharge', 'Floor Incharge'],
        roleSections: { 'Section Incharge': ['Dispatch', 'Dispatch Section'], Incharge: ['Dispatch', 'Dispatch Section'], 'Floor Incharge': ['Dispatch', 'Dispatch Section'] } },
      { id: 'jobber-master', title: 'Jobber Master',
        description: 'Jobbers for job orders & job-work e-way bills — GSTIN, address, PIN · Excel upload & export',
        icon: Factory, route: '/admin/jobber-master', color: 'bg-amber-700',
        moduleId: 'eway_bills', ops: ['ewb-generate'], opId: 'jobber-master', roles: ['Site Admin', 'Section Incharge', 'Incharge', 'Floor Incharge'],
        roleSections: { 'Section Incharge': ['Dispatch', 'Dispatch Section'], Incharge: ['Dispatch', 'Dispatch Section'], 'Floor Incharge': ['Dispatch', 'Dispatch Section'] } },
      { id: 'pt-files', title: 'Distributor PT Files',
        description: 'Shoper goods-inward (PT) file for each shipped distributor invoice',
        icon: FileCode2, route: '/eway-bills/pt-files', color: 'bg-emerald-700',
        moduleId: 'eway_bills', ops: ['ewb-generate'], opId: 'pt-files', roles: ['Site Admin', 'Section Incharge', 'Incharge', 'Floor Incharge'],
        roleSections: { 'Section Incharge': ['Dispatch', 'Dispatch Section'], Incharge: ['Dispatch', 'Dispatch Section'], 'Floor Incharge': ['Dispatch', 'Dispatch Section'] } },
      { id: 'transporter-returns', title: 'Transporter Returns',
        description: 'Record returned dispatches & re-dispatch under the same DC',
        icon: Undo2, route: '/admin/transporter-returns', color: 'bg-rose-600',
        adminOnly: true, opId: 'transporter-returns' },
      { id: 'print-codes', title: 'Print Codes',
        description: 'Generate barcodes/QR codes',
        icon: Printer, route: '/print-codes', color: 'bg-teal-500',
        moduleId: 'print_codes' },
      { id: 'purchase-orders', title: 'Purchase Orders',
        description: 'Create, issue, receive POs & track vendor performance',
        icon: FileText, route: '/po', color: 'bg-violet-500',
        moduleId: 'purchase_orders' },
      { id: 'purchase-requisitions', title: 'Purchase Requisitions',
        description: 'Post what you need — every PO is raised against a requisition',
        icon: ClipboardList, route: '/pr', color: 'bg-indigo-500',
        moduleId: 'purchase_orders' },
      { id: 'incoming-consignments', title: 'Incoming Consignments',
        description: 'View and request consignments',
        icon: Package, route: '/incoming-consignments', color: 'bg-emerald-600',
        moduleId: 'incoming_consignments',
        roles: ['Section Incharge', 'Section Supervisor'] },
      { id: 'shipped-transfers', title: 'Shipped Transfers',
        description: 'Incoming internal transfers',
        icon: Truck, route: '/shipped-transfers', color: 'bg-indigo-600',
        moduleId: 'shipped_transfers',
        roles: ['Section Incharge', 'Section Supervisor'] },
      { id: 'category-incharge', title: 'Category Incharge',
        description: 'Create shipment requests',
        icon: Package, route: '/category/incharge', color: 'bg-fuchsia-500',
        moduleId: 'category_incharge',
        roles: ['Category Incharge'] },
      { id: 'category-manager', title: 'Category Manager',
        description: 'Review & approve shipment requests',
        icon: Package, route: '/category/manager', color: 'bg-pink-500',
        moduleId: 'category_manager',
        roles: ['Category Manager'] },
      { id: 'section-shipment-requests', title: 'Section Shipment Requests',
        description: 'Approved shipments for your section',
        icon: Truck, route: '/shipment-requests/section', color: 'bg-purple-500',
        moduleId: 'section_shipment_requests',
        roles: ['Section Incharge', 'Section Supervisor'] },
    ],
  },

  // === WMS Operations ===
  {
    title: 'WMS Operations',
    icon: Warehouse,
    modules: [
      { id: 'sfg-wms', title: 'SFG WMS', description: 'Semi-Finished Goods put-away & picking',
        icon: Archive, route: '/wms/sfg', color: 'bg-indigo-500',
        moduleId: 'sfg_wms' },
      { id: 'fg-wms', title: 'FG WMS', description: 'Finished Goods put-away & picking',
        icon: PackageCheck, route: '/wms/fg', color: 'bg-emerald-500',
        moduleId: 'fg_wms' },
      { id: 're-sticker-queue', title: 'Re-Sticker Queue',
        description: 'Bulk reprint FG stickers after MRP / price revisions',
        icon: Printer, route: '/admin/re-sticker-queue', color: 'bg-amber-500',
        moduleId: 'fg_wms' },
      { id: 'wms-setup', title: 'WMS Setup', description: 'Location hierarchy, zones & settings',
        icon: Settings, route: '/wms', color: 'bg-violet-600',
        adminOnly: true, opId: 'wms-setup' },
    ],
  },

  // === Warehouse Sections ===
  {
    title: 'Warehouse Sections',
    icon: Warehouse,
    modules: [
      { id: 'fob-section', title: 'FOB Section', description: 'Bundle receipt, tagging & packing',
        icon: Package, route: '/sections/fob', color: 'bg-blue-500',
        moduleId: 'fob', sections: ['FOB Section', 'FOB', 'Fob'] },
      { id: 'greige-section', title: 'Greige Section', description: 'Sort assignment & inspection',
        icon: Factory, route: '/sections/greige', color: 'bg-emerald-500',
        moduleId: 'greige', sections: ['Greige Section', 'Greige'] },
      { id: 'greige-storage', title: 'Greige Storage', description: 'Put away, picking & shipments',
        icon: Warehouse, route: '/sections/greige-storage', color: 'bg-teal-500',
        moduleId: 'greige_storage', sections: ['Greige Storage', 'Greige_Storage'] },
      { id: 'goods-transfer', title: 'Goods Transfer', description: 'Label printing & FG assignment',
        icon: Truck, route: '/sections/goods-transfer', color: 'bg-cyan-500',
        moduleId: 'goods_transfer', sections: ['Goods Transfer', 'Goods_Transfer'] },
      { id: 'processing-section', title: 'Processing Section', description: 'Processing units & transfers',
        icon: Settings, route: '/sections/processing', color: 'bg-violet-500',
        moduleId: 'processing', sections: ['Processing Section', 'Processing'] },
      { id: 'inhouse-section', title: 'Inhouse Section', description: 'In-house processing operations',
        icon: Home, route: '/sections/inhouse', color: 'bg-indigo-500',
        moduleId: 'inhouse', sections: ['Inhouse', 'Inhouse Section'] },
      { id: 'unassorted-section', title: 'Unassorted Section', description: 'Unsorted goods handling',
        icon: Boxes, route: '/sections/unassorted', color: 'bg-amber-500',
        moduleId: 'unassorted', sections: ['Unassorted', 'Unassorted Section'] },
      { id: 'tissue-section', title: 'Tissue Section', description: 'Tissue product handling',
        icon: TestTube, route: '/sections/tissue', color: 'bg-pink-500',
        moduleId: 'tissue', sections: ['Tissue', 'Tissue Section'] },
      { id: 'flexi-section', title: 'Flexi Section', description: 'Flexible packaging ops',
        icon: Sparkles, route: '/sections/flexi', color: 'bg-purple-500',
        moduleId: 'flexi', sections: ['Flexi', 'Flexi Section'] },
      { id: 'shirt-mfg-section', title: 'Shirt Manufacturing', description: 'Shirt production workflow',
        icon: Shirt, route: '/sections/shirt-manufacturing', color: 'bg-rose-500',
        moduleId: 'shirt_manufacturing', sections: ['Shirt Manufacturing', 'Shirt_Manufacturing'] },
      { id: 'sample-section', title: 'Sample Section', description: 'Sample management',
        icon: TestTube, route: '/sections/sample', color: 'bg-lime-500',
        moduleId: 'sample', sections: ['Sample', 'Sample Section'] },
      { id: 'sales-return-section', title: 'Sales Return', description: 'Return processing',
        icon: Undo2, route: '/sections/sales-return', color: 'bg-orange-500',
        moduleId: 'sales_return', sections: ['Sales Return', 'Sales_Return'] },
      { id: 'accounts-section', title: 'Accounts Section', description: 'Financial tracking',
        icon: Calculator, route: '/sections/accounts', color: 'bg-sky-500',
        moduleId: 'accounts', sections: ['Accounts', 'Accounts Section'] },
      { id: 'trims-section', title: 'Trims Section', description: 'Trims inventory',
        icon: Scissors, route: '/sections/trims', color: 'bg-fuchsia-500',
        moduleId: 'trims', sections: ['Trims', 'Trims Section'] },
      { id: 'housekeeping-section', title: 'House Keeping', description: 'Housekeeping supplies',
        icon: Home, route: '/sections/house-keeping', color: 'bg-green-500',
        moduleId: 'house_keeping', sections: ['House Keeping', 'House_Keeping', 'Housekeeping'] },
      { id: 'electrical-section', title: 'Electrical Goods', description: 'Electrical items inventory',
        icon: Zap, route: '/sections/electrical-goods', color: 'bg-yellow-500',
        moduleId: 'electrical_goods', sections: ['Electrical Goods', 'Electrical_Goods'] },
      { id: 'it-goods-section', title: 'IT Goods', description: 'IT equipment tracking',
        icon: Monitor, route: '/sections/it-goods', color: 'bg-blue-600',
        moduleId: 'it_goods', sections: ['IT Goods', 'IT_Goods', 'IT'] },
      { id: 'rnm-section', title: 'R & M', description: 'Repair & Maintenance',
        icon: Wrench, route: '/sections/rnm', color: 'bg-slate-500',
        moduleId: 'rnm', sections: ['R & M', 'R&M', 'RnM', 'RNM'] },
      { id: 'ecom-section', title: 'ECOM Section', description: 'E-commerce fulfillment',
        icon: ShoppingCart, route: '/sections/ecom', color: 'bg-red-500',
        moduleId: 'ecom', sections: ['ECOM', 'Ecom', 'E-Commerce'] },
      { id: 'yarn-section', title: 'Yarn Section', description: 'Yarn inventory',
        icon: CircleDot, route: '/sections/yarn', color: 'bg-amber-600',
        moduleId: 'yarn', sections: ['Yarn', 'Yarn Section'] },
      { id: 'admin-section-section', title: 'Admin Section', description: 'Admin parcels & documents',
        icon: Briefcase, route: '/sections/admin-section', color: 'bg-stone-600',
        moduleId: 'admin_section', sections: ['Admin Section'] },
      { id: 'weaving-section', title: 'Weaving Section', description: 'Weaving parcels & documents',
        icon: Grid3x3, route: '/sections/weaving', color: 'bg-cyan-700',
        moduleId: 'weaving', sections: ['Weaving', 'Weaving Section'] },
      { id: 'dispatch-section', title: 'Dispatch Section', description: 'Dispatch operations',
        icon: Send, route: '/sections/dispatch', color: 'bg-indigo-600',
        moduleId: 'dispatch', sections: ['Dispatch', 'Dispatch Section'] },
    ],
  },

  // === Internal Transport ===
  {
    title: 'Internal Transport',
    icon: Truck,
    modules: [
      { id: 'transport-console', title: 'Internal Transport',
        description: 'Fleet console — trips, fuel, service, attendance & requests',
        icon: Truck, route: '/sections/transport', color: 'bg-amber-600',
        moduleId: 'transport', roles: ['Transport Manager', 'Driver'],
        sections: ['Transport', 'Transport Section'] },
      { id: 'transport-request-vehicle', title: 'Request a Vehicle',
        description: 'Raise a vehicle request for your section — auto-assigned when a vehicle is free',
        icon: Send, route: '/sections/transport/request-vehicle', color: 'bg-orange-500',
        moduleId: 'transport',
        roles: ['Transport Manager', 'Driver', 'Section Incharge', 'Section Supervisor', 'Category Incharge', 'Category Manager'] },
    ],
  },

  // === Staff Attendance (section-wise, selfie + supervisor approval) ===
  {
    title: 'Staff Attendance',
    icon: UserCheck,
    modules: [
      { id: 'my-attendance', title: 'My Attendance',
        description: 'Check In / Check Out with a selfie',
        icon: UserCheck, route: '/attendance/my', color: 'bg-emerald-600',
        moduleId: 'staff_attendance', ops: ['satt-my'] },
      { id: 'attendance-approvals', title: 'Attendance Approvals',
        description: 'Approve your section\'s check-in / check-out selfies, reference photos & section devices',
        icon: ClipboardList, route: '/attendance', color: 'bg-amber-600',
        moduleId: 'staff_attendance', ops: ['satt-approve'],
        roles: ['Section Incharge', 'Section Supervisor', 'Supervisor', 'Floor Supervisor', 'Incharge', 'Floor Incharge'] },
      { id: 'attendance-reports', title: 'Attendance Reports',
        description: 'Daily & monthly attendance, late / off-site flags, CSV download',
        icon: BarChart3, route: '/attendance?tab=daily', color: 'bg-sky-600',
        moduleId: 'staff_attendance', ops: ['satt-reports'] },
      { id: 'manpower-requests', title: 'Manpower Requests',
        description: 'Request men / women, send free staff, accept arrivals, transfer staff, Contract Pool',
        icon: Users, route: '/attendance?tab=manpower', color: 'bg-amber-700',
        moduleId: 'staff_attendance', ops: ['satt-approve', 'satt-reports', 'satt-hr'], opId: 'staff-attendance-admin',
        roles: ['HR Manager', 'Section Incharge', 'Section Supervisor', 'Supervisor', 'Floor Supervisor', 'Incharge', 'Floor Incharge'] },
      { id: 'manday-report', title: 'Manday & Manhour Report',
        description: 'Mandays, manhours & Internal Transfer qty out per section per day — CSV',
        icon: BarChart3, route: '/attendance?tab=manday', color: 'bg-teal-700',
        moduleId: 'staff_attendance', ops: ['satt-approve', 'satt-reports', 'satt-hr'], opId: 'staff-attendance-admin',
        roles: ['HR Manager', 'Section Incharge', 'Section Supervisor', 'Supervisor', 'Floor Supervisor', 'Incharge', 'Floor Incharge'] },
      { id: 'attendance-headcount', title: 'Section Headcount',
        description: 'Live In Roll vs present per section — absentees, waiting approval, TV mode',
        icon: Activity, route: '/attendance?tab=headcount', color: 'bg-rose-600',
        moduleId: 'staff_attendance', ops: ['satt-approve', 'satt-reports', 'satt-hr'], opId: 'staff-attendance-admin',
        roles: ['HR Manager', 'Section Incharge', 'Section Supervisor', 'Supervisor', 'Floor Supervisor', 'Incharge', 'Floor Incharge'] },
      { id: 'staff-attendance-hr', title: 'Staff Attendance (HR)',
        description: 'All sections — approvals, daily / monthly, work locations, shifts & settings',
        icon: Users, route: '/attendance?tab=daily', color: 'bg-slate-700',
        moduleId: 'staff_attendance', ops: ['satt-hr'], roles: ['HR Manager'], opId: 'staff-attendance-admin' },
      { id: 'attendance-device', title: 'Attendance Device',
        description: 'Open the section punching screen on this tablet — sets it up first if needed',
        icon: MonitorSmartphone, route: '/attendance/kiosk', color: 'bg-emerald-700',
        moduleId: 'staff_attendance', ops: ['satt-approve', 'satt-hr'], opId: 'staff-attendance-admin',
        roles: ['HR Manager', 'Section Incharge', 'Section Supervisor', 'Supervisor', 'Floor Supervisor', 'Incharge', 'Floor Incharge'] },
      { id: 'contract-payroll', title: 'Contract Payroll',
        description: 'Contract workers\' days & hours per section by agency for any date range — bill amount, CSV & PDF',
        icon: IndianRupee, route: '/attendance?tab=payroll', color: 'bg-emerald-800',
        moduleId: 'staff_attendance', ops: ['satt-hr', 'satt-payroll'], roles: ['HR Manager'], opId: 'staff-attendance-admin' },
      { id: 'staff-master-hr', title: 'Staff Master',
        description: 'Create & edit staff — gender, Regular / Contract, In Roll / Left · CSV import & export',
        icon: Contact, route: '/admin/staff-master', color: 'bg-indigo-600',
        moduleId: 'staff_attendance', ops: ['satt-hr'], roles: ['HR Manager'], opId: 'staff-attendance-admin' },
      { id: 'operation-rankings', title: 'Staff Rankings by Operation',
        description: 'Monthly pieces per day / hour / % target per operation — flags staff low 3 months in a row',
        icon: Trophy, route: '/attendance/operation-rankings', color: 'bg-indigo-600',
        moduleId: 'accounts', ops: ['hourly-staff-report', 'payslip'], roles: ['Site Admin'] },
    ],
  },

  // === Reports & Analytics (admin-only by default) ===
  {
    title: 'Reports & Analytics',
    icon: BarChart3,
    modules: [
      { id: 'inward-reports', title: 'Inward Reports', description: 'View and manage all inward entries',
        icon: BarChart3, route: '/admin/inward-reports', color: 'bg-emerald-500',
        adminOnly: true, opId: 'inward-reports' },
      { id: 'customer-outward-reports', title: 'Customer Outward Reports',
        description: 'Customer dispatch gate pass records',
        icon: Truck, route: '/admin/customer-outward-reports', color: 'bg-orange-500',
        adminOnly: true, opId: 'customer-outward-reports' },
      { id: 'outward-reports', title: 'Outward Reports',
        description: 'Job Worker & Internal Transfer records',
        icon: Truck, route: '/admin/outward-reports', color: 'bg-rose-500',
        adminOnly: true, opId: 'outward-reports' },
      { id: 'goods-transfer-reports', title: 'Goods Transfer Reports',
        description: 'Packing & FG Assignment Analytics',
        icon: ClipboardList, route: '/admin/goods-transfer-reports', color: 'bg-cyan-500',
        adminOnly: true, opId: 'goods-transfer-reports' },
      { id: 'fob-reports', title: 'FOB Section Reports',
        description: 'Assortment, Stock & Packing Analytics',
        icon: ClipboardList, route: '/admin/fob-reports', color: 'bg-indigo-500',
        adminOnly: true, opId: 'fob-reports' },
      { id: 'wms-reports', title: 'WMS Reports', description: 'SFG & FG storage inventory reports',
        icon: BarChart3, route: '/admin/wms-reports', color: 'bg-violet-500',
        adminOnly: true, opId: 'wms-reports' },
      { id: 'internal-transfer-report', title: 'Internal Transfer Report',
        description: 'All transfers across modules — date range, destination filter, CSV export',
        icon: ArrowLeftRight, route: '/reports/internal-transfers', color: 'bg-purple-600',
        adminOnly: true, opId: 'internal-transfer-report' },
      { id: 'operation-wise-stock-report', title: 'Operation Wise Stock Report',
        description: 'WIP stock at each operation per section — cascading filters, CSV export',
        icon: BarChart3, route: '/reports/operation-wise-stock', color: 'bg-emerald-600',
        adminOnly: true, opId: 'operation-wise-stock-report' },
      { id: 'storage-ageing-report', title: 'Stock Ageing Report',
        description: 'Greige / SFG / FG stock by age range (0-30 … >365 days, editable), Excel export',
        icon: Clock, route: '/reports/storage-ageing', color: 'bg-amber-600',
        adminOnly: true, opId: 'storage-ageing-report' },
      { id: 'dwell-time-report', title: 'Dwell Time Report',
        description: 'Time between operations — section dwell (receipt → transfer) + dispatch op-wise, CSV export',
        icon: Timer, route: '/reports/dwell-time', color: 'bg-indigo-600',
        adminOnly: true, opId: 'section-dwell-report' },
      { id: 'staff-performance', title: 'Staff Performance',
        description: 'Track productivity and work metrics',
        icon: TrendingUp, route: '/admin/staff-performance', color: 'bg-orange-500',
        adminOnly: true, opId: 'staff-performance' },
      { id: 'all-usn-details', title: 'All USN Details',
        description: 'Consolidated USN report with lookups',
        icon: FileText, route: '/admin/all-usn-details', color: 'bg-fuchsia-500',
        adminOnly: true, opId: 'all-usn-details' },
      { id: 'fetch-document-number', title: 'Fetch Document Number',
        description: 'Scan a bundle to trace its inward document, gate-in, section receipt & accounts handover',
        icon: FileText, route: '/reports/fetch-document-number', color: 'bg-sky-600',
        opId: 'fetch-document-number' },
      { id: 'dispatch-section-reports', title: 'Dispatch Section Reports',
        description: 'PIN-protected staff performance for management',
        icon: Send, route: '/admin/dispatch-reports', color: 'bg-teal-600',
        adminOnly: true, opId: 'dispatch-section-reports' },
      { id: 'usn-tracker', title: 'USN / Bundle Tracker',
        description: 'Trace USN or Bundle lifecycle from Gate Entry to Outward',
        icon: Search, route: '/admin/usn-tracker', color: 'bg-slate-700',
        adminOnly: true, opId: 'usn-tracker' },
      { id: 'usn-replacement-log', title: 'USN Replacement Log',
        description: 'Audit log of every reprint→replacement (old USN deactivated, new USN issued)',
        icon: RefreshCw, route: '/admin/usn-replacement-log', color: 'bg-rose-600',
        adminOnly: true, opId: 'usn-replacement-log' },
      { id: 'inhouse-bundle-reconciliation', title: 'Inhouse Bundle Reconciliation',
        description: 'Fix legacy bundles where Bundle Receipt qty ≠ Processing Inward qty',
        icon: PackageCheck, route: '/admin/inhouse-bundle-reconciliation', color: 'bg-amber-600',
        adminOnly: true, opId: 'inhouse-bundle-reconciliation' },
    ],
  },

  // === System Setup ===
  {
    title: 'System Setup',
    icon: Settings,
    modules: [
      { id: 'greige-storage-setup', title: 'Greige Storage Setup',
        description: 'Hall, Floor, Row, Position & Capacity',
        icon: MapPin, route: '/sections/greige-storage/location-setup', color: 'bg-teal-500',
        adminOnly: true, opId: 'greige-storage-setup' },
      { id: 'label-config', title: 'Label Configuration',
        description: 'Configure printer label positions',
        icon: Settings, route: '/admin/label-config', color: 'bg-indigo-500',
        adminOnly: true, opId: 'label-config' },
      { id: 'main-label-templates', title: 'Main Label Templates',
        description: 'Configure product label layouts',
        icon: Settings, route: '/admin/main-label-templates', color: 'bg-amber-500',
        adminOnly: true, opId: 'main-label-templates' },
      { id: 'print-settings', title: 'Print Settings',
        description: 'Arial font sizes & global print options',
        icon: Settings, route: '/admin/print-settings', color: 'bg-orange-500',
        adminOnly: true, opId: 'print-settings' },
      { id: 'printer-settings', title: 'Printer Settings',
        description: 'Network printer configuration for auto-print',
        icon: Printer, route: '/admin/printer-settings', color: 'bg-teal-500',
        adminOnly: true, opId: 'printer-settings' },
      { id: 'print-manager', title: 'Print Manager',
        description: 'JavaScript-based label printing (No WebUSB)',
        icon: Printer, route: '/print-manager', color: 'bg-green-500',
        moduleId: 'print_manager' },
      { id: 'messenger', title: 'Internal Messenger',
        description: 'Real-time team communication',
        icon: MessageSquare, route: '/messenger', color: 'bg-blue-500' },
      { id: 'transaction-dashboard', title: 'Transaction Dashboard',
        description: 'Real-time inward/outward across all sections',
        icon: Activity, route: '/transaction-dashboard', color: 'bg-purple-500',
        adminOnly: true, opId: 'transaction-dashboard' },
      { id: 'operations-manager', title: 'Operations Manager',
        description: 'Expose operations in User Management & USN/Bundle Tracker',
        icon: Settings, route: '/admin/operations-manager', color: 'bg-slate-600',
        adminOnly: true, opId: 'operations-manager' },
      { id: 'print-stations', title: 'Print Stations',
        description: 'Network label bridge — print to USB TSC printers from any device',
        icon: Printer, route: '/admin/print-stations', color: 'bg-emerald-600',
        adminOnly: true, opId: 'print-stations' },
    ],
  },

  // === Data Management (admin-only) ===
  {
    title: 'Data Management',
    icon: Database,
    modules: [
      { id: 'imports', title: 'Import Data', description: 'Import roles and sites from CSV',
        icon: Upload, route: '/admin/import', color: 'bg-green-500',
        adminOnly: true, opId: 'imports' },
      { id: 'import-logs', title: 'Manage Imports', description: 'View import history and logs',
        icon: FileText, route: '/admin/manage-imports', color: 'bg-purple-500',
        adminOnly: true, opId: 'import-logs' },
      { id: 'staff-master', title: 'Staff Master',
        description: 'Create & edit staff — gender, Regular / Contract, In Roll / Left · CSV import & export',
        icon: Contact, route: '/admin/staff-master', color: 'bg-indigo-600',
        adminOnly: true, opId: 'staff-master' },
      { id: 'usn-export', title: 'USN Master Export', description: 'Download USN data from all modules',
        icon: Database, route: '/admin/usn-export', color: 'bg-teal-500',
        adminOnly: true, opId: 'usn-export' },
      { id: 'database', title: 'Database Management', description: 'View sequences and reset data',
        icon: Database, route: '/admin/database', color: 'bg-slate-600',
        adminOnly: true, opId: 'database' },
      { id: 'reassign-location', title: 'Reassign Location',
        description: 'Assign bins to STORED bundles with blank location',
        icon: MapPin, route: '/admin/reassign-location', color: 'bg-orange-500',
        adminOnly: true, opId: 'reassign-location' },
      { id: 'backup', title: 'Backup & Restore', description: 'Database backup and restore',
        icon: HardDrive, route: '/admin/backup', color: 'bg-teal-600',
        adminOnly: true, opId: 'backup' },
    ],
  },

  // === Entry Management (admin-only — destructive) ===
  {
    title: 'Entry Management',
    icon: Trash2,
    modules: [
      { id: 'inward-entry-management', title: 'Inward Entries', description: 'Delete Inward entries',
        icon: FileInput, route: '/admin/inward-entry-management', color: 'bg-emerald-600',
        adminOnly: true, opId: 'inward-entry-management' },
      { id: 'outward-entry-management', title: 'Outward Entries', description: 'Delete Outward entries',
        icon: FileOutput, route: '/admin/outward-entry-management', color: 'bg-rose-600',
        adminOnly: true, opId: 'outward-entry-management' },
      { id: 'fob-entry-management', title: 'FOB Entries', description: 'Delete FOB entries by entry number',
        icon: Trash2, route: '/admin/fob-entry-management', color: 'bg-red-600',
        adminOnly: true, opId: 'fob-entry-management' },
      { id: 'goods-transfer-management', title: 'Goods Transfer Entries',
        description: 'Delete Goods Transfer entries',
        icon: Truck, route: '/admin/goods-transfer-management', color: 'bg-cyan-600',
        adminOnly: true, opId: 'goods-transfer-management' },
      { id: 'greige-section-entry-management', title: 'Greige Section Entries',
        description: 'Manage & move entries to draft',
        icon: Package, route: '/admin/greige-section-entry-management', color: 'bg-amber-600',
        adminOnly: true, opId: 'greige-section-entry-management' },
    ],
  },

  // === Documentation ===
  {
    title: 'Documentation',
    icon: BookOpen,
    modules: [
      { id: 'system-docs', title: 'System Documentation',
        description: 'Complete module reference & workflows',
        icon: BookOpen, route: '/admin/system-documentation', color: 'bg-slate-600',
        adminOnly: true, opId: 'system-docs' },
    ],
  },

];


// ---------------------------------------------------------------------------
// Visibility filter
// ---------------------------------------------------------------------------
function isSuperAdmin(user) {
  if (!user) return false;
  const r = (user.role || '').toLowerCase();
  return r === 'admin' || r === 'super_admin';
}

function userHasAdminGrant(user, opId) {
  if (!opId) return false;
  const grants = user?.allowed_operations?.admin_panel || [];
  return Array.isArray(grants) && grants.includes(opId);
}

function tileVisibleForUser(tile, user) {
  if (isSuperAdmin(user)) return true;
  if (!user) return false;

  // Op-level tiles (e.g. Staff Attendance ticks): role list OR a ticked op OR a legacy admin-panel grant.
  if (tile.ops) {
    const granted = user?.allowed_operations?.[tile.moduleId] || [];
    const need = tile.roleSections?.[user.role];
    const secs = (Array.isArray(user.sections) ? user.sections : []).map((s) => String(s).trim().toLowerCase());
    const roleOk = Array.isArray(tile.roles) && tile.roles.includes(user.role) && (!need || need.some((s) => secs.includes(s.toLowerCase())));
    return roleOk || tile.ops.some((o) => granted.includes(o)) || userHasAdminGrant(user, tile.opId);
  }

  // Admin-only tile: hidden unless granted via allowed_operations.admin_panel.
  if (tile.adminOnly) return userHasAdminGrant(user, tile.opId);

  // 2026-05-15 — Roles + moduleId now combine as OR (was: roles overrides
  // moduleId, which meant SuperAdmin couldn't grant a role-restricted
  // tile via User Management → Modules & Operations). Users matching
  // the role list see the tile; users with an explicit module grant
  // also see it. Section matches still respected when present.
  const userRole = user?.role;
  const userModules = Array.isArray(user.modules) ? user.modules : [];
  const userSections = Array.isArray(user.sections) ? user.sections : [];
  const roleOk = Array.isArray(tile.roles) && tile.roles.length > 0
    && tile.roles.includes(userRole);
  const moduleOk = !!tile.moduleId && userModules.includes(tile.moduleId);
  const sectionOk = Array.isArray(tile.sections) && tile.sections.length > 0
    && tile.sections.some((s) => userSections.includes(s));

  if (roleOk) return true;
  if (tile.moduleId && tile.sections) return moduleOk || sectionOk;
  if (tile.sections) return sectionOk;
  if (tile.moduleId) return moduleOk;
  // No gating fields at all → tile is open (legacy behaviour preserved).
  return !tile.roles;
}


// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({ totalUsers: 0, totalRoles: 0, totalSites: 0, totalImports: 0 });
  const [enabledDispatchSections, setEnabledDispatchSections] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const me = await authApi.getMe();
        setUser(me);
        // Per-site dispatch sections (Phase B) — fail-soft if endpoint errors.
        try {
          const siteId = (typeof localStorage !== 'undefined' && localStorage.getItem('active_site_id')) || 'salem';
          const r = await apiClient.get(`/dispatch-sections/sites/${siteId}/enabled`);
          setEnabledDispatchSections(r.data?.enabled || []);
        } catch (_) {
          setEnabledDispatchSections([]);
        }
        // Stats — only for Super Admin (or those granted 'stats-cards').
        const showStats = isSuperAdmin(me) || userHasAdminGrant(me, 'stats-cards');
        if (showStats) {
          try {
            const [usersData, rolesData, sitesData, importsData] = await Promise.all([
              authApi.getUsers(),
              authApi.getRoles(),
              authApi.getSites(),
              authApi.getImportLogs(),
            ]);
            setStats({
              totalUsers: usersData.total || 0,
              totalRoles: rolesData.roles?.length || 0,
              totalSites: sitesData.sites?.length || 0,
              totalImports: importsData.total || 0,
            });
          } catch (_) {
            // Stats failure shouldn't break the dashboard for the user.
          }
        }
      } catch (e) {
        console.error('Error fetching dashboard data:', e);
        navigate('/login');
      }
    })();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await authApi.logout();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Logout failed');
    }
  };

  const showStats = user && (isSuperAdmin(user) || userHasAdminGrant(user, 'stats-cards'));

  // Phase B — dynamic per-site dispatch tiles (color_combo / art_silk / kids).
  // Tile is shown when:
  //   • the active site has the section enabled, AND
  //   • the user is Super Admin OR has been granted the per-section module
  //     id `dispatch_${slug}` via User Management → Modules & Operations.
  //
  // 2026-05-20 fix: previously the dynamic tile carried `moduleId: 'dispatch'`,
  // which meant a user granted ONLY "Color Combo Section" (module id
  // `dispatch_color_combo`) did not see the tile on /admin/dashboard because
  // their `user.modules` did not contain the generic `dispatch`.
  // We now use the per-section module id (`dispatch_${c.slug}`) so the grant
  // and the gate line up.
  const dynamicDispatchGroup = (() => {
    if (!user || !enabledDispatchSections.length) return null;
    const tiles = PER_SECTION_DISPATCH_CATALOG
      .filter((c) => enabledDispatchSections.includes(c.slug))
      .map((c) => ({
        id: `dispatch-${c.slug}`,
        title: c.title,
        description: 'Dedicated dispatch flow · SFG / FG storage · packing',
        icon: Send,
        route: `/sections/dispatch/${c.slug}`,
        color: c.color,
        // Granular per-section grant — matches the module id surfaced by
        // /api/admin/modules for `dispatch_color_combo` / `dispatch_art_silk` /
        // `dispatch_kids`. Section fallback still allows legacy users who
        // were given the section name directly (e.g. "Color Combo Section").
        moduleId: `dispatch_${c.slug}`,
        sections: ['Dispatch', 'Dispatch Section', c.title],
      }));
    if (!tiles.length) return null;
    return {
      title: 'Dispatch Sections',
      icon: Send,
      modules: tiles,
    };
  })();

  const baseGroups = dynamicDispatchGroup ? [...moduleGroups, dynamicDispatchGroup] : moduleGroups;
  const visibleGroups = baseGroups
    .map((g) => ({ ...g, modules: g.modules.filter((m) => tileVisibleForUser(m, user)) }))
    .filter((g) => g.modules.length > 0);

  const totalVisibleTiles = visibleGroups.reduce((s, g) => s + g.modules.length, 0);

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Package className="w-8 h-8" />
            <div>
              <h1 className="text-xl font-bold font-display">Dashboard</h1>
              <p className="text-sm text-[#E8DCC8]">WarehouseOps Pro</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {user && (
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium" data-testid="dashboard-user-name">{user.name}</p>
                <p className="text-xs text-[#E8DCC8]" data-testid="dashboard-user-role">{user.role}</p>
              </div>
            )}
            <Button onClick={handleLogout} variant="ghost"
              className="text-[#F9FAFB] hover:bg-white/10" data-testid="logout-button">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Stats Cards (Super Admin only) */}
        {showStats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-testid="dashboard-stats">
            <StatCard label="Total Users" value={stats.totalUsers} Icon={Users} color="text-blue-500" />
            <StatCard label="User Roles" value={stats.totalRoles} Icon={BarChart3} color="text-green-500" />
            <StatCard label="Sites" value={stats.totalSites} Icon={Package} color="text-purple-500" />
            <StatCard label="Imports" value={stats.totalImports} Icon={Upload} color="text-orange-500" />
          </div>
        )}

        {/* Empty-state */}
        {user && totalVisibleTiles === 0 && (
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-8 text-center" data-testid="dashboard-empty-state">
            <Bell className="w-10 h-10 mx-auto text-amber-500 mb-2" />
            <p className="text-base font-semibold text-[#1A1F2E]">No tiles available yet</p>
            <p className="text-sm text-[#5A5F6E] mt-1">
              Your account has no modules, sections, or admin grants assigned.
              Ask an administrator to grant access via User Management.
            </p>
          </div>
        )}

        {/* Grouped Tiles */}
        <div className="space-y-10">
          {visibleGroups.map((group) => {
            const GroupIcon = group.icon;
            return (
              <div key={group.title}>
                <div className="flex items-center gap-3 mb-4">
                  <GroupIcon className="w-5 h-5 text-[#2D5F4F]" />
                  <h2 className="text-xl font-bold text-[#1A1F2E] font-display">{group.title}</h2>
                  <div className="flex-1 h-px bg-[#E5DFD0]" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {group.modules.map((m) => {
                    const Icon = m.icon;
                    return (
                      <div
                        key={m.id}
                        onClick={() => navigate(m.route)}
                        className="bg-white rounded-lg border border-[#E5DFD0] hover:border-[#2D5F4F] shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden group"
                        data-testid={`module-${m.id}`}
                      >
                        <div className={`${m.color} p-4 flex items-center gap-3`}>
                          <Icon className="w-8 h-8 text-white" />
                          <h3 className="text-base font-semibold text-white font-display truncate">
                            {m.title}
                          </h3>
                        </div>
                        <div className="p-3">
                          <p className="text-xs text-[#5A5F6E] line-clamp-2">{m.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
};


function StatCard({ label, value, Icon, color }) {
  return (
    <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-[#5A5F6E]">{label}</p>
          <p className="text-3xl font-bold text-[#1A1F2E] mt-1">{value}</p>
        </div>
        <Icon className={`w-10 h-10 ${color}`} />
      </div>
    </div>
  );
}

export default AdminDashboard;
